import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import queryString from "query-string";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useExportJournalEntries, useJournalEntries } from "../../api";
import { EthiopianDateField } from "../EthiopianDateField";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 14px;
  font-weight: 500;

  span {
    color: #aaa;
  }
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 16px;
`;

export const LedgerTable = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault("")
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault("")
  );

  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const { chartOfAccountId } = useParams();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const { accountName, accountNumber, accountType } = queryParams;

  const journalEntriesQuery = useJournalEntries({
    churchId: selectedChurch?.id,
    chartOfAccountId,
    page,
    pageSize,
    queries: {
      startDate,
      endDate
    }
  });

  const { mutate: exportJournalEntries, isLoading: isExporting } =
    useExportJournalEntries();

  const columns = [
    {
      field: "id",
      headerName: "Transaction ID",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{row.transaction_number}</span>;
      }
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{moment(row.entry_date).format("DD-MM-YYYY")}</span>;
      }
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1
    },
    {
      field: "debit",
      headerName: "Debit",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <span>
            {row.entry_type === "debit"
              ? Number(row.amount).toLocaleString()
              : ""}
          </span>
        );
      }
    },
    {
      field: "credit",
      headerName: "Credit",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <span>
            {row.entry_type === "credit"
              ? Number(row.amount).toLocaleString()
              : ""}
          </span>
        );
      }
    },

    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    }
  ];

  return (
    <Container>
      <Stack alignItems="flex-start" spacing={4}>
        <Header>
          <Stack>
            <Title>
              <span>Account Name</span> - {accountName}
            </Title>
            <Title>
              <span>Account ID</span> - {accountNumber}
            </Title>
            <Title>
              <span>Account Type</span> - {accountType}
            </Title>
            <Title>
              <span>Report Type</span> - General Ledger
            </Title>
          </Stack>
          <PrimaryActions>
            <EthiopianDateField
              label="Start Date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              width="auto"
            />
            <EthiopianDateField
              label="End Date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              width="auto"
            />
            <Button
              onClick={() =>
                exportJournalEntries({
                  churchId: selectedChurch?.id,
                  chartOfAccountId,
                  queries: {
                    startDate,
                    endDate
                  }
                })
              }
              disabled={isExporting}
            >
              Export
            </Button>
          </PrimaryActions>
        </Header>
      </Stack>
      <DataGrid
        autoHeight
        rows={journalEntriesQuery.data?.result?.data || []}
        rowCount={journalEntriesQuery.data?.result?.total || 0}
        loading={journalEntriesQuery?.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#E6F7F8",
            borderRadius: "10px 10px 0px 0px",
            color: "#000000",
            border: "none"
          }
        }}
      />
    </Container>
  );
};
